import {isLifeCycleAtLeast, RevaLifeCycleState} from "@reva/shared/lifecycle-state";

import {RevaPermission} from "./revaPermission";

/** Term of DNF, consisting of a conjunction of permissions */
export type PermissionConjunction = RevaPermission | RevaPermission[];

/** Rule in DNF (disjunctive normal form), consisting of conjunctive terms */
export type PermissionRule = RevaPermission | PermissionConjunction[];

/**
 * A permission requirement whose rule depends on whether a read-only snapshot is currently
 * loaded. Snapshots hold already-computed results and run no indications, so a page may drop or
 * relax its permission check in the snapshot case. Leave a side undefined to require no permission
 * in that case.
 */
export interface SnapshotAwarePermissionRule {
    snapshot?: PermissionRule;
    nonSnapshot?: PermissionRule;
}

/** Either a flat permission rule or a snapshot-dependent one. */
export type PermissionRequirement = PermissionRule | SnapshotAwarePermissionRule;

export const PERMISSION_RULE_LOCATION: PermissionRule = [
    RevaPermission.LOCATION_MICROMACRO,
    RevaPermission.LOCATION_POIS,
    RevaPermission.LOCATION_CLIMATERISKS,
    RevaPermission.LOCATION_NOISE,
    RevaPermission.LOCATION_EMISSION,
    RevaPermission.REVA_UI_CADASTRE,
];

export const PERMISSION_RULE_CADASTRE: PermissionRule = RevaPermission.REVA_UI_CADASTRE;

export const PERMISSION_RULE_LOCATION_MICRO: PermissionRule = RevaPermission.LOCATION_MICROMACRO;
export const PERMISSION_RULE_LOCATION_MACRO: PermissionRule = RevaPermission.LOCATION_MICROMACRO;
export const PERMISSION_RULE_LOCATION_POIS: PermissionRule = RevaPermission.LOCATION_POIS;

export const PERMISSION_RULE_LOCATION_MICROMACRO_TEXT: PermissionRule = RevaPermission.LOCATION_MICROMACRO_TEXT;

export const PERMISSION_RULE_CLIMATERISK: PermissionRule = RevaPermission.LOCATION_CLIMATERISKS;

export const PERMISSION_RULE_LOCATION_NOISE: PermissionRule = RevaPermission.LOCATION_NOISE;
export const PERMISSION_RULE_LOCATION_EMISSION: PermissionRule = RevaPermission.LOCATION_EMISSION;

/** #19552: Tab Emissionen schon mit einem der beiden Rechte; er zeigt dann nur die erlaubten Blöcke. */
export const PERMISSION_RULE_EMISSION_TAB: PermissionRule = [
    RevaPermission.LOCATION_NOISE,
    RevaPermission.LOCATION_EMISSION,
];

/** #19552: Reiter Markt, sobald einer seiner Daten-Tabs erlaubt ist (Beschreibung hat keine eigene Regel). */
export const PERMISSION_RULE_MARKET: PermissionRule = [
    RevaPermission.LOCATION_TIMELINES,
    RevaPermission.LOCATION_PRICEINDEX,
    RevaPermission.LOCATION_MARKETDATA,
];

export const PERMISSION_RULE_MARKET_TIMELINES: PermissionRule = RevaPermission.LOCATION_TIMELINES;

export const PERMISSION_RULE_MARKET_PRICEINDEX: PermissionRule = RevaPermission.LOCATION_PRICEINDEX;

export const PERMISSION_RULE_MARKET_MARKETDATA: PermissionRule = RevaPermission.LOCATION_MARKETDATA;

export const PERMISSION_RULE_VALUE: PermissionRule = RevaPermission.INDICATION_VALUE;

/**
 * Value permission requirement that only applies to live (non-snapshot) valuations. A snapshot
 * carries the already-computed indication result, so viewing it requires no indication permission.
 */
export const PERMISSION_REQUIREMENT_VALUE_NON_SNAPSHOTS: PermissionRequirement = {
    nonSnapshot: PERMISSION_RULE_VALUE,
};

export const PERMISSION_RULE_CORRECTION: PermissionRule = [
    RevaPermission.ADMINS,
    RevaPermission.REVA_LIFECYCLE_CORRECTION,
];

/** #19113: gates the "Admin" start-page tab (all users' valuations/snapshots/statistics). */
export const PERMISSION_RULE_OWNERSHIP_ADMIN: PermissionRule = [
    RevaPermission.ADMINS,
    RevaPermission.REVA_OWNERSHIP_ADMIN,
];

/**
 * #19125: gates the exclusive "Alle Bewertungen" revision start-page tab (route /start/revision). Deliberately
 * NOT including ADMINS — this tab is shown ONLY to the read-only revision role, which sees this single tab instead
 * of the five normal start-page tabs. Admins keep the regular tabs.
 */
export const PERMISSION_RULE_REVISION: PermissionRule = RevaPermission.REVA_LIFECYCLE_REVISION_READONLY;

/**
 * Assigning a valuation's owner to ANOTHER person needs reva-ownership-assign (ADMINS implicitly included) —
 * assigning to the pool (owner = null) or to oneself is allowed for everyone except the read-only revision
 * role (see backend LifeCycleGuards ASSIGN, which blocks that role ahead of any operation-specific check;
 * PanelRevaOrder hides the assignment dropdown for it entirely via isRevisionReadOnly).
 */
export const PERMISSION_RULE_OWNERSHIP_ASSIGN: PermissionRule = [
    RevaPermission.ADMINS,
    RevaPermission.REVA_OWNERSHIP_ASSIGN,
];

/**
 * #19481: REVA-IDs mit Zweck Research anlegen; schaltet auch den Startseiten-Reiter „Research-Abfragen" frei.
 * Bewusst OHNE ADMINS, anders als die übrigen Regeln dieser Datei: bei den Zweck-Rechten zählt nur das direkte
 * Recht (siehe Backend LifeCycleGuards#isPurposePermitted).
 */
export const PERMISSION_RULE_PURPOSE_RESEARCH: PermissionRule = RevaPermission.REVA_PURPOSE_RESEARCH;

/**
 * #19481: REVA-IDs mit Zweck Bewertung anlegen und Research-Abfragen per Zweckwechsel in Bewertungen umwandeln.
 * Bewusst OHNE ADMINS (siehe {@link PERMISSION_RULE_PURPOSE_RESEARCH}).
 */
export const PERMISSION_RULE_PURPOSE_VALUATION: PermissionRule = RevaPermission.REVA_PURPOSE_VALUATION;

export const PERMISSION_RULE_DELETION: PermissionRule = [RevaPermission.ADMINS, RevaPermission.REVA_LIFECYCLE_DELETION];

/**
 * Ob eine Bewertung in diesem Lebenszyklus-Zustand noch löschbar ist: nur in New/DataInput, ab PreFinal für niemanden,
 * auch nicht mit {@link PERMISSION_RULE_DELETION} (#19530; Spiegel von LifeCycleGuards#checkDeletableState ohne die
 * Autotest-Lizenz-Ausnahme).
 */
export function isDeletableLifeCycleState(state: RevaLifeCycleState | null | undefined): boolean {
    return !isLifeCycleAtLeast(state, RevaLifeCycleState.PreFinal);
}

/**
 * Ob in diesem Lebenszyklus-Zustand nur Personen mit {@link PERMISSION_RULE_FINALIZATION} Owner werden dürfen: in
 * PreFinal und Correction (#19302 Z-B2; Spiegel von RevaValuationsRegistry#isAssignableAsOwnerInState). WIP und Pool
 * filtern dort nach dem Schreibrecht, eine anderen Personen zugewiesene Bewertung wäre sonst unauffindbar.
 */
export function isOwnershipRestrictedToFinalizers(state: RevaLifeCycleState | null | undefined): boolean {
    return state === RevaLifeCycleState.PreFinal || state === RevaLifeCycleState.Correction;
}

/**
 * Ob eine Bewertung in diesem Lebenszyklus-Zustand inhaltlich bearbeitet werden darf (#19302 V-B8; Spiegel von
 * LifeCycleGuards#isContentWriteAllowedInState ohne die Read-only-Revisionsrolle, die prüft
 * `useCanWriteValuationContent`): in New/DataInput immer, in PreFinal/Correction nur mit
 * {@link PERMISSION_RULE_FINALIZATION}, in Final nie.
 *
 * @param canFinalize ob der User {@link PERMISSION_RULE_FINALIZATION} hat
 */
export function isContentWriteAllowedInState(
    state: RevaLifeCycleState | null | undefined,
    canFinalize: boolean,
): boolean {
    if (state === RevaLifeCycleState.New || state === RevaLifeCycleState.DataInput) {
        return true;
    }
    return isOwnershipRestrictedToFinalizers(state) && canFinalize;
}

export const PERMISSION_RULE_FINALIZATION: PermissionRule = [
    RevaPermission.ADMINS,
    RevaPermission.REVA_LIFECYCLE_FINALIZATION,
];

export const PERMISSION_RULE_PRE_FINALIZATION: PermissionRule = [
    RevaPermission.ADMINS,
    RevaPermission.REVA_LIFECYCLE_PRE_FINALIZATION,
];

export const PERMISSION_RULE_DOC_LIMITED_SCOPE: PermissionRule = [
    RevaPermission.ADMINS,
    RevaPermission.REVA_DOC_LIMITED_SCOPE,
];

export const PERMISSION_RULE_DOC_FULL_BELWERTV24: PermissionRule = [
    RevaPermission.ADMINS,
    RevaPermission.REVA_DOC_FULL_BELWERTV_24,
];

export const PERMISSION_RULE_DOC_FULL_PFANDBG16: PermissionRule = [
    RevaPermission.ADMINS,
    RevaPermission.REVA_DOC_FULL_PFANDBG_16,
];

/**
 * Energieausweis-IDP in REVA (automatisches Auslesen hochgeladener Energieausweise): Auto-Trigger
 * beim Upload, Rerun-Button und Ergebnis-Übernahme. Admins behalten das Feature implizit.
 */
export const PERMISSION_RULE_IDP_ENERGY: PermissionRule = [RevaPermission.ADMINS, RevaPermission.REVA_IDP_ENERGY];

/** KI-Chat mit kiano: nur Admins, dieselbe Berechtigung wie der Tab „Energieausweis-Daten (Admins-only)“. */
export const PERMISSION_RULE_CHAT: PermissionRule = RevaPermission.ADMINS;

/**
 * Gate for the appreciation UI (#18607): the whole appreciation feature is enabled for users
 * with EITHER reva-doc-full-pfandbg-16 OR reva-doc-full-belwertv-24 (admins included).
 */
export const PERMISSION_RULE_APPRECIATIONS: PermissionRule = [
    RevaPermission.ADMINS,
    RevaPermission.REVA_DOC_FULL_PFANDBG_16,
    RevaPermission.REVA_DOC_FULL_BELWERTV_24,
];

/**
 * Check if a permission rule is granted by the permissions, the user has.
 * @param grantedPermissions set of permissions from the user
 * @param rule permission rule to check
 * @return whether the permission check was successful
 */
export function checkPermissionRule(grantedPermissions: RevaPermission[], rule: PermissionRule | undefined): boolean {
    if (!rule || (Array.isArray(rule) && rule.length === 0)) return true;
    if (!Array.isArray(rule)) {
        return grantedPermissions.includes(rule);
    }
    for (const conjunction of rule) {
        if (!Array.isArray(conjunction)) {
            if (grantedPermissions.includes(conjunction)) return true;
            continue;
        }
        if (conjunction.length === 0) return true;
        if (conjunction.every((permission) => grantedPermissions.includes(permission))) return true;
    }
    return false;
}

/**
 * Resolve a {@link PermissionRequirement} to the concrete {@link PermissionRule} that applies for
 * the current snapshot state. Returns undefined (i.e. "no permission required") when the matching
 * side of a snapshot-dependent rule is left unset.
 * @param requirement flat rule or snapshot-dependent rule
 * @param isSnapshot whether a read-only snapshot is currently loaded
 */
export function resolvePermissionRequirement(
    requirement: PermissionRequirement | undefined,
    isSnapshot: boolean,
): PermissionRule | undefined {
    if (requirement !== undefined && typeof requirement === "object" && !Array.isArray(requirement)) {
        return isSnapshot ? requirement.snapshot : requirement.nonSnapshot;
    }
    return requirement;
}
